import type { ComponentType, FC, ReactNode } from 'react'

import { useEffect, useId, useRef, useState } from 'react'
import Image from 'next/image'
import {
    CalendarCheck,
    CircleCheck,
    FileCheck2,
    FileText,
    Mail,
    PenLine,
    Send
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollLitHeading } from '@/components/generals/scroll-lit-heading'

type IconComponent = ComponentType<{ className?: string }>

interface Feature {
    title: string
    description: string
    points: Array<string>
    origin: string
    scale: number
}

interface OverlayProps {
    className?: string
    children: ReactNode
}

const FEATURES: Array<Feature> = [
    {
        title: 'Contracts turn into timelines on their own',
        description: 'Upload an executed contract and Closewell reads every page, counteroffer and addendum.',
        points: ['Every contingency date calculated', 'Calendar invites sent to each party'],
        origin: '36% 84%',
        scale: 1.22
    },
    {
        title: 'Signatures get chased while you show homes',
        description: 'Unsigned addenda follow up by email and text, on a schedule you set.',
        points: ['Polite nudges, never pushy', 'Signed copies filed to the deal'],
        origin: '24% 26%',
        scale: 1.16
    },
    {
        title: 'Clients hear the news before they ask',
        description: 'Weekly updates for buyers and sellers, written in your voice and sent with your approval.',
        points: ['Plain-language status for clients', 'Lender and title kept in sync'],
        origin: '68% 62%',
        scale: 1.2
    },
    {
        title: 'The compliance file builds itself',
        description: "Each document is checked against your brokerage's checklist the moment it arrives.",
        points: ['Missing initials and pages flagged', 'A clean file, ready for broker review'],
        origin: '50% 50%',
        scale: 1.03
    }
]

const Chip: FC<OverlayProps> = ({ className, children }): ReactNode => {
    return (
        <span className={cn('absolute inline-flex animate-in items-center gap-1.5 rounded-full border border-white/50 bg-white/60 px-3 py-1 text-[11.5px] font-medium text-foreground/80 backdrop-blur-md delay-200 duration-700 fill-mode-both fade-in slide-in-from-top-3', className)}>
            {children}
        </span>
    )
}

const Card: FC<OverlayProps> = ({ className, children }): ReactNode => {
    return (
        <div className={cn('absolute animate-in rounded-xl border border-white/60 bg-white/85 p-3.5 text-left shadow-[0_18px_40px_-18px_rgba(35,34,30,0.45)] backdrop-blur-md delay-300 duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] fill-mode-both fade-in zoom-in-95 slide-in-from-bottom-6', className)}>
            {children}
        </div>
    )
}

const CheckRow: FC<{ icon: IconComponent, children: ReactNode, done?: boolean }> = ({ icon: Icon, children, done = false }): ReactNode => {
    return (
        <p className='flex items-center gap-2 text-xs text-foreground/80'>
            <Icon className={cn('size-3.5 shrink-0', done ? 'text-leaf' : 'text-mist')} />
            {children}
        </p>
    )
}

const Overlay: FC<{ index: number }> = ({ index }): ReactNode => {
    if (index === 0) {
        return (
            <>
                <Chip className='top-4 left-4'>
                    <FileText className='size-3.5' />
                    32 pages read · 11 dates found
                </Chip>

                <Chip className='top-4 right-4 hidden sm:inline-flex'>
                    <span className='soft-pulse size-1.5 rounded-full bg-leaf' />
                    Live
                </Chip>

                <Card className='bottom-4 left-4 w-[17rem] max-w-[calc(100%-2rem)]'>
                    <div className='flex items-center gap-3'>
                        <span className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#f1eee6] text-foreground/75'>
                            <CalendarCheck className='size-4' />
                        </span>

                        <div>
                            <p className='text-[13px] font-semibold text-foreground'>
                                Inspection period ends
                            </p>

                            <p className='text-xs text-mist'>
                                Oct 19 · invites sent to 4 people
                            </p>
                        </div>
                    </div>
                </Card>
            </>
        )
    }

    if (index === 1) {
        return (
            <>
                <Chip className='top-4 left-4'>
                    <PenLine className='size-3.5' />
                    2 signatures outstanding
                </Chip>

                <Card className='bottom-4 left-4 w-[18rem] max-w-[calc(100%-2rem)] space-y-2'>
                    <p className='text-[13px] font-semibold text-foreground'>
                        Repair addendum
                    </p>

                    <CheckRow icon={Send}>
                        Reminder sent by text · 9:14 am
                    </CheckRow>

                    <CheckRow icon={CircleCheck} done>
                        Sellers signed · 11:02 am
                    </CheckRow>
                </Card>
            </>
        )
    }

    if (index === 2) {
        return (
            <>
                <Chip className='top-4 right-4'>
                    <Mail className='size-3.5' />
                    Written in your voice
                </Chip>

                <Card className='bottom-4 left-4 w-[21rem] max-w-[calc(100%-2rem)]'>
                    <p className='text-[11px] text-mist'>
                        Draft to Sam & Jordan Patel
                    </p>

                    <p className='mt-1.5 text-[13px] leading-snug text-foreground'>
                        Good news: the appraisal came in at value. Next up is final loan approval by Nov 3. Nothing needed from you this week.
                    </p>

                    <div className='mt-3 flex gap-2'>
                        <span className='rounded-md bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground'>
                            Approve & send
                        </span>

                        <span className='rounded-md border border-[#e3e0d6] bg-white px-2.5 py-1 text-[11px] font-medium text-foreground/80'>
                            Edit
                        </span>
                    </div>
                </Card>
            </>
        )
    }

    return (
        <>
            <Chip className='top-4 right-4'>
                <FileCheck2 className='size-3.5' />
                File complete
            </Chip>

            <Card className='bottom-4 left-4 w-[18rem] max-w-[calc(100%-2rem)]'>
                <div className='flex items-center justify-between'>
                    <p className='text-[13px] font-semibold text-foreground'>
                        Broker review
                    </p>

                    <span className='text-xs font-medium text-leaf'>
                        18 of 18
                    </span>
                </div>

                <div className='mt-2 h-1.5 overflow-hidden rounded-full bg-[#ebe8df]'>
                    <div className='h-full w-full rounded-full bg-leaf' />
                </div>

                <div className='mt-3 space-y-1.5'>
                    <CheckRow icon={CircleCheck} done>
                        Initials on every page
                    </CheckRow>

                    <CheckRow icon={CircleCheck} done>
                        Lead paint disclosure signed
                    </CheckRow>

                    <CheckRow icon={CircleCheck} done>
                        Wire instructions verified by phone
                    </CheckRow>
                </div>
            </Card>
        </>
    )
}

export const FeatureTabs: FC = (): ReactNode => {
    const baseId = useId()
    const sectionRef = useRef<HTMLElement>(null)
    const [active, setActive] = useState(0)
    const [cycle, setCycle] = useState(0)
    const [inView, setInView] = useState(false)
    const [hovering, setHovering] = useState(false)

    useEffect(() => {
        const element = sectionRef.current

        if (!element || typeof IntersectionObserver === 'undefined') return

        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.3 })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    const select = (index: number) => {
        setActive(index)
        setCycle(value => value + 1)
    }

    const advance = () => setActive(index => (index + 1) % FEATURES.length)

    const paused = !inView || hovering
    const current = FEATURES[active]

    return (
        <section id='features' ref={sectionRef} aria-labelledby='features-title' className='px-5 pt-28 sm:pt-36'>
            <div className='mx-auto max-w-3xl text-center'>
                <ScrollLitHeading
                    id='features-title'
                    lines={[{ text: 'The busywork,' }, { text: 'quietly handled', italic: true }]}
                    className='text-[clamp(3rem,6.4vw,5.25rem)] leading-[0.95] tracking-[-0.03em]'
                />

                <p className='reveal mx-auto mt-6 max-w-[34rem] text-[17px] leading-[1.5] text-muted-foreground sm:text-lg'>
                    Closewell takes the follow-ups off your desk and checks in before anything important goes out. You stay in charge of every deal.
                </p>
            </div>

            <div className='mx-auto mt-14 grid max-w-6xl items-start gap-8 sm:mt-20 lg:grid-cols-[minmax(0,23rem)_minmax(0,1fr)] lg:gap-10'>
                <div
                    onMouseEnter={() => setHovering(true)}
                    onMouseLeave={() => setHovering(false)}
                    data-reveal='left'
                    className='reveal lg:pt-2'
                >
                    {FEATURES.map((feature, index) => {
                        const isActive = index === active
                        const buttonId = `${baseId}-tab-${index}`
                        const panelId = `${baseId}-panel-${index}`

                        return (
                            <div key={feature.title} className='relative border-t border-border'>
                                {isActive && (
                                    <span
                                        key={cycle}
                                        aria-hidden='true'
                                        onAnimationEnd={advance}
                                        className={cn('feature-progress-run absolute -top-px left-0 h-[1.5px] w-full bg-foreground/80', paused && 'feature-progress-paused')}
                                    />
                                )}

                                <h3>
                                    <button
                                        id={buttonId}
                                        type='button'
                                        aria-expanded={isActive}
                                        aria-controls={panelId}
                                        onClick={() => select(index)}
                                        className='flex w-full items-start gap-5 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background'
                                    >
                                        <span className={cn('pt-1 text-xs tabular-nums transition-colors duration-300', isActive ? 'text-foreground' : 'text-mist')}>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>

                                        <span className={cn('max-w-[17rem] text-[17px] leading-snug font-medium transition-[color,translate] duration-300 ease-out', isActive ? 'translate-x-1 text-foreground' : 'text-foreground/70 hover:translate-x-1 hover:text-foreground')}>
                                            {feature.title}
                                        </span>
                                    </button>
                                </h3>

                                <div
                                    id={panelId}
                                    role='region'
                                    aria-labelledby={buttonId}
                                    inert={!isActive}
                                    className={cn('grid transition-[grid-template-rows,opacity] duration-500 ease-out', isActive ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}
                                >
                                    <div className='overflow-hidden'>
                                        <div className='pb-6 pl-10'>
                                            <p className={cn('text-[15px] leading-relaxed text-muted-foreground', isActive && 'animate-in delay-100 duration-700 fill-mode-both fade-in slide-in-from-bottom-2')}>
                                                {feature.description}
                                            </p>

                                            <ul className='mt-4 space-y-2'>
                                                {feature.points.map((point, pointIndex) => (
                                                    <li
                                                        key={point}
                                                        className={cn('flex items-center gap-2 text-[14px] text-foreground/85', isActive && 'animate-in duration-500 fill-mode-both fade-in slide-in-from-left-3')}
                                                        style={isActive ? { animationDelay: `${260 + pointIndex * 140}ms` } : undefined}
                                                    >
                                                        <CircleCheck className={cn('size-4 shrink-0 transition-colors delay-500 duration-500', isActive ? 'text-leaf' : 'text-mist')} />
                                                        {point}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div data-reveal='scale' className='reveal reveal-d2 relative aspect-[3/2] overflow-hidden rounded-2xl bg-[#e8dcc2] shadow-[0_30px_60px_-36px_rgba(60,48,24,0.55)]'>
                    <Image
                        src='/images/still-life.jpg'
                        alt='Painting of a sunlit table by an open window, with a ring of brass keys on a folded contract, a mug of coffee and a pot of rosemary'
                        fill
                        sizes='(min-width: 1024px) 720px, 100vw'
                        className='object-cover transition-[transform,transform-origin] duration-[1600ms] ease-out motion-reduce:transition-none'
                        style={{ transform: `scale(${current.scale})`, transformOrigin: current.origin }}
                    />

                    <div key={active} aria-hidden='true' className='absolute inset-0'>
                        <Overlay index={active} />
                    </div>
                </div>
            </div>
        </section>
    )
}
